/**
 * Filtrado de las tablas de Asignaciones (posiciones abiertas y posiciones asignadas).
 *
 * DECISIONES QUE VIVEN AQUÍ
 *
 * 1. **Un solo motor para las dos pestañas.** Las filas tienen formas distintas
 *    (`OpenPositionRow` y `AssignedPositionRow`), así que cada una se proyecta a
 *    `FilaFiltrable` y el filtro es el mismo. Dos motores parecidos se desincronizan: ya pasó
 *    con el diccionario de banderas copiado en tres pantallas (BITACORA §10).
 *
 * 2. **El filtro es del cliente, sobre lo ya cargado.** Antes la pestaña "Asignadas" filtraba
 *    fecha y PDV en el servidor: con filtros por seis columnas eso serían seis idas y vueltas,
 *    dos caminos distintos (unos con espera, otros instantáneos) y el mismo dato contado dos
 *    veces. La lectura ya trae la campaña entera paginada, así que filtrar en memoria es
 *    instantáneo y las dos pestañas se comportan igual.
 *
 * 3. **Un filtro vacío no filtra.** Cada campo en blanco deja pasar todo; los filtros se
 *    combinan con Y (todos deben cumplirse), que es lo que la gente espera de una barra así.
 *
 * 4. **El texto se compara sin acentos ni mayúsculas.** En la base hay "Maria Laura Navarro
 *    Sanchez" (el formulario de registro llegó sin tildes) y quien busca escribe "María".
 *    Comparando en crudo, buscar bien el nombre no encuentra a la persona.
 */

/** Guion largo: el mismo carácter que muestran las tablas, para que el select coincida. */
const GUION = '–';

/** Sin acentos, sin mayúsculas y sin espacios de borde. */
export function normalizarTexto(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** ¿Alguno de los campos contiene lo buscado? Búsqueda vacía deja pasar todo. */
export function coincideTexto(
  busqueda: string,
  ...campos: (string | null | undefined)[]
): boolean {
  const q = normalizarTexto(busqueda);
  if (q === '') return true;
  return campos.some((c) => c != null && normalizarTexto(c).includes(q));
}

/**
 * ¿Estan TODAS las palabras buscadas, en cualquiera de los campos? Busqueda vacia deja pasar todo.
 *
 * Es distinta de `coincideTexto`, y la diferencia importa: esa exige que la busqueda ENTERA aparezca
 * en UN campo, asi que «flor surco» no encuentra nada —ningun campo dice las dos cosas—. Esta junta
 * todos los campos en un solo pajar y pide que cada palabra este en alguna parte, asi que
 * «flor surco» encuentra a Flor en Makro Surco y no a todas las Flor.
 *
 * Vivia dentro de `Validacion.tsx` como funcion local. Se saco aca cuando la bandeja de cierres
 * necesito «la casilla de filtro que tambien tiene la pestana de validacion»: dos casillas que se
 * ven iguales y se comportan distinto es peor que no tener la segunda, y copiar la funcion garantiza
 * que se desincronicen (ya paso con el diccionario de banderas en tres pantallas, BITACORA §10).
 */
export function coincideTodasLasPalabras(
  busqueda: string,
  ...campos: (string | null | undefined)[]
): boolean {
  const q = normalizarTexto(busqueda);
  if (q === '') return true;
  const pajar = normalizarTexto(campos.filter((c) => c != null).join(' '));
  return q.split(/\s+/).every((palabra) => pajar.includes(palabra));
}

/**
 * El primero y el ultimo dia de un mes `yyyy-MM`.
 *
 * Es el atajo que el usuario pidio en Validacion —elegir un mes fija el rango y busca— y que ahora
 * comparte la bandeja de cierres. `Date.UTC(y, m, 0)` da el ultimo dia del mes `m` porque el dia 0
 * del mes siguiente ES el ultimo del anterior; sin UTC, una zona al oeste de Greenwich devuelve el
 * dia anterior y el rango se queda corto por uno.
 */
export function rangoDelMes(mes: string): { desde: string; hasta: string } | null {
  if (!/^\d{4}-\d{2}$/.test(mes)) return null;
  const [y, m] = mes.split('-').map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

/** Rango inclusivo sobre fechas ISO (`yyyy-MM-dd`), comparables como cadena. */
export function enRangoDeFechas(date: string, desde: string, hasta: string): boolean {
  if (desde !== '' && date < desde) return false;
  if (hasta !== '' && date > hasta) return false;
  return true;
}

/** `11:00:00` + `19:00:00` → `11:00–19:00`. */
export function formatoHorario(startTime: string, endTime: string): string {
  return `${startTime.slice(0, 5)}${GUION}${endTime.slice(0, 5)}`;
}

/** Proyección común de una fila de cualquiera de las dos tablas. */
export interface FilaFiltrable {
  date: string;
  /** Clave del PDV con la que trabaja el desplegable (código o id, según la pestaña). */
  pdvKey: string;
  horario: string;
  slot: number | null;
  /** Estado de la asignación viva, o `null` si la posición todavía no tiene recurso. */
  estado: string | null;
  nombre: string | null;
  dni: string | null;
}

/**
 * `estado`: `''` todas · `'sin'` sin recurso · cualquier otro valor, el estado exacto
 * (`propuesta`, `aprobada`). El resto de los campos: `''` no filtra.
 */
export interface FiltrosTabla {
  desde: string;
  hasta: string;
  pdv: string;
  horario: string;
  slot: string;
  estado: string;
  texto: string;
}

export const FILTROS_VACIOS: FiltrosTabla = {
  desde: '', hasta: '', pdv: '', horario: '', slot: '', estado: '', texto: '',
};

/** ¿Hay algún filtro puesto? Decide si se muestra "Limpiar filtros". */
export function hayFiltros(f: FiltrosTabla): boolean {
  return Object.values(f).some((v) => v !== '');
}

export function pasaFiltro(fila: FilaFiltrable, f: FiltrosTabla): boolean {
  if (!enRangoDeFechas(fila.date, f.desde, f.hasta)) return false;
  if (f.pdv !== '' && fila.pdvKey !== f.pdv) return false;
  if (f.horario !== '' && fila.horario !== f.horario) return false;
  if (f.slot !== '' && String(fila.slot ?? '') !== f.slot) return false;
  if (f.estado === 'sin' && fila.estado !== null) return false;
  if (f.estado !== '' && f.estado !== 'sin' && fila.estado !== f.estado) return false;
  if (!coincideTexto(f.texto, fila.nombre, fila.dni)) return false;
  return true;
}

/** Valores distintos de un campo, ordenados, para armar un desplegable. */
export function opcionesDe<K extends keyof FilaFiltrable>(
  filas: FilaFiltrable[],
  campo: K,
): string[] {
  const vistos = new Set<string>();
  for (const fila of filas) {
    const v = fila[campo];
    if (v !== null && v !== undefined && v !== '') vistos.add(String(v));
  }
  return [...vistos].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
}
